import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PERMISSIONS } from '@oxxean/types';
import { fetchSyncStatus } from '../../lib/api/sync';
import { queryKeys } from '../../lib/query-client';
import { formatDateTime } from '../../lib/format';
import { useAuth } from '../../hooks/useAuth';
import { DataBoundary } from '../../components/DataBoundary';
import { SyncSourceDetail } from '../../components/SyncStatusIndicator';
import { Card, Notice, Skeleton } from '../../components/primitives';
import { Icon } from '../../components/Icon';
import { NAV_ITEMS } from '../../routes/navigation';
import { PageHeading } from '../layout/PageHeading';

/**
 * Inicio.
 *
 * Deliberadamente NO muestra cifras: su función es decir en qué estado está la
 * información antes de que alguien lea un indicador, y llevar a cada módulo
 * autorizado. Un dato sin contexto es exactamente el problema que esta plataforma
 * corrige.
 */
export function HomePage(): JSX.Element {
  const { user, can } = useAuth();

  const statusQuery = useQuery({
    queryKey: queryKeys.syncStatus,
    queryFn: fetchSyncStatus,
    enabled: can(PERMISSIONS.SYNC_VIEW),
  });

  const accessible = NAV_ITEMS.filter(
    (item) => item.path !== '/' && (item.permission === undefined || can(item.permission)),
  );

  const firstName = (user?.fullName ?? '').split(/\s+/)[0] ?? '';

  return (
    <>
      <PageHeading
        title={firstName.length > 0 ? `Hola, ${firstName}` : 'Inicio'}
        description={
          <>
            People Analytics OXXEAN 360 consolida BUK y BUK Asistencia en modo de sólo lectura. Cada
            indicador viaja con su estado: <strong>Actualizado</strong>, <strong>Parcial</strong>,{' '}
            <strong>Sin registros</strong>, <strong>No disponible</strong> o{' '}
            <strong>Registro manual</strong>.
          </>
        }
      />

      {user !== null && user.companyScope.length > 0 && (
        <Notice tone="info">
          Su acceso está limitado a {user.companyScope.length} empresa(s)
          {user.areaScope.length > 0 ? ` y ${user.areaScope.length} área(s)` : ''}. Los indicadores se
          consultan ya filtrados por ese ámbito.
        </Notice>
      )}

      <div className="row row--2">
        <Card
          title="Estado de las fuentes"
          hint="Antes de leer un indicador, conviene saber si su origen está al día."
        >
          {can(PERMISSIONS.SYNC_VIEW) ? (
            <DataBoundary
              isLoading={statusQuery.isLoading}
              isError={statusQuery.isError}
              error={statusQuery.error}
              onRetry={() => void statusQuery.refetch()}
              isEmpty={statusQuery.data !== undefined && statusQuery.data.length === 0}
              empty={{
                title: 'Sin orígenes configurados',
                message:
                  'Todavía no hay integraciones registradas. Configúrelas en el módulo de configuración.',
              }}
              skeleton={
                <div className="stack">
                  <Skeleton height={78} radius={10} />
                  <Skeleton height={78} radius={10} />
                </div>
              }
            >
              <div className="sync-list">
                {(statusQuery.data ?? []).map((item) => (
                  <SyncSourceDetail key={item.source} item={item} />
                ))}
              </div>
            </DataBoundary>
          ) : (
            <p className="muted tiny">
              Su perfil no incluye el permiso de consulta de sincronizaciones, por lo que el estado de
              las fuentes no se muestra aquí.
            </p>
          )}
        </Card>

        <Card title="Su sesión">
          <dl className="def-list">
            <dt>Nombre</dt>
            <dd>{user?.fullName ?? '—'}</dd>
            <dt>Correo</dt>
            <dd>{user?.email ?? '—'}</dd>
            <dt>Roles</dt>
            <dd>{user?.roles.join(', ') ?? '—'}</dd>
            <dt>Permisos</dt>
            <dd>{user?.permissions.length ?? 0} otorgados</dd>
            <dt>Último acceso</dt>
            <dd>{formatDateTime(user?.lastLoginAt ?? null)}</dd>
          </dl>
          <p className="tiny muted" style={{ marginTop: 12 }}>
            La sesión se mantiene con cookies protegidas que el navegador no expone a JavaScript. No
            comparta su cuenta: toda acción queda registrada en la auditoría.
          </p>
        </Card>
      </div>

      <Card title="Módulos disponibles" hint="Sólo aparecen los módulos que su perfil autoriza.">
        <div className="tile-grid">
          {accessible.map((item) => (
            <Link className="tile" key={item.key} to={item.path}>
              <span className="inline">
                <Icon name={item.icon} size={16} />
                <span className="tile__name">{item.label}</span>
              </span>
              <span className="tile__desc">{item.description}</span>
            </Link>
          ))}
        </div>
      </Card>
    </>
  );
}
